import { useCreditDetails } from "@components/app/app-header/credits-dropdown-menu/hooks/use-credit-details"
import { Box } from "@components/Box"
import { Button } from "@components/ui/button"
import { Field } from "@components/ui/form/Field"
import { Link } from "@components/ui/link"
import { ModalOrSidebar } from "@components/ui/modal-sidebar"
import { Text } from "@components/ui/typograhpy/Text"
import { cancelReasons } from "@lib/cancel-reasons"
import { ActivePlanType } from "@lib/get-active-plan-type"
import { getGraphqlClient } from "@lib/graphql-client"
import { extractErrorCode, parseError } from "@lib/parse-error"
import { useDispatch } from "@lib/store"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { Form, Formik } from "formik"
import React, { FC, ReactElement, useState } from "react"

type FormValues = {
  reason: string
}
const { sdk } = getGraphqlClient()

type CancelSubscriptionModalProps = {
  periodEnd: string
  status: "idle" | "disabled"
  activePlanType?: ActivePlanType
  trigger?: ReactElement
}

export const CancelSubscriptionModal: FC<CancelSubscriptionModalProps> = ({
  periodEnd,
  status,
  activePlanType,
  trigger,
}) => {
  const [open, setOpen] = useState(false)
  const dispatch = useDispatch()
  const [error, setError] = useState<undefined | string>(undefined)
  const queryClient = useQueryClient()
  const credits = useCreditDetails()
  const cancelSubscription = useMutation({
    mutationFn: async ({ reason }: FormValues) => {
      await sdk.CancelSubscription({
        input: {
          reason,
        },
      })
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["account"] })
    },
  })

  return (
    <ModalOrSidebar
      open={open}
      onOpenChange={setOpen}
      title="Downgrade plan"
      trigger={
        trigger ?? (
          <Button variant="secondary" width="fill" status={status}>
            {activePlanType === "ENTERPRISE"
              ? "Enterprise plan is active"
              : "Downgrade plan"}
          </Button>
        )
      }
    >
      <Text variant="regular" className="mb-6">
        {`If you switch to the Try-out plan, you will lose all the credits you have from your current plan after ${periodEnd}. Are you sure you want to proceed?`}
      </Text>

      {credits?.remainingCreditsThisPeriod && (
        <Box className="mb-2 flex justify-between bg-opacity p-4">
          <Text variant="regular">Current credit value</Text>
          <Text variant="regular">{`$${(
            (credits?.remainingCreditsThisPeriod ?? 0) * 0.018
          ).toFixed(2)}`}</Text>
        </Box>
      )}

      {activePlanType === "BUSINESS" && (
        <Text variant="regular" className="mb-5 text-border-grey">
          You can keep your credits by downgrading to the basic subscription
        </Text>
      )}

      <Formik
        initialValues={{
          reason: "",
        }}
        validateOnChange={true}
        onSubmit={handleSubmit}
      >
        {() => (
          <Form>
            <Text variant="regular" className="mb-4">
              Please let us now why you’re downgrading.
            </Text>
            <Field
              field="select"
              name="reason"
              label="Select reason"
              className="mb-8"
              validation={{
                required: true,
              }}
            >
              <option value="" disabled>
                Choose an option
              </option>
              {cancelReasons.map(({ label, value }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Field>
            {error && (
              <Text variant="regular" className="mb-8 text-danger">
                {error}
              </Text>
            )}
            <div className="mb-8 flex items-center justify-between">
              <Link as="button" onClick={() => setOpen((val) => !val)}>
                Cancel
              </Link>
              <Button
                variant="danger"
                status={cancelSubscription.isPending ? "loading" : "idle"}
                type="submit"
              >
                Yes, downgrade my plan
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </ModalOrSidebar>
  )

  async function handleSubmit(formValues: FormValues) {
    const formattedReason =
      cancelReasons.find((v) => v.value === formValues.reason)?.label || ""

    try {
      await cancelSubscription.mutateAsync({
        reason: formattedReason,
      })
      setError(undefined)
      setOpen(false)
      dispatch({
        type: "add-toast",
        payload: {
          variant: "success",
          title: "Successfully cancelled your subscription.",
        },
      })
      setOpen(false)
    } catch (error) {
      setError(parseError(extractErrorCode(error)))
      if (process.env.NODE === "development") {
        console.error(error)
      }
    }
  }
}
